import { Button, Drawer, IconButton, Input, Select } from "@heathmont/moon-core-tw";
import {
  ControlsCloseSmall,
  GenericAlarmRound,
} from "@heathmont/moon-icons-tw";
import React, { useState } from "react";
import moment from "moment";

const LEAVE_TYPES = [
  { name: "Annual Leave", type: "AL" },
  { name: "Sick Leave", type: "SL" },
  { name: "Compensatory Off", type: "CO" },
  { name: "Maternity Off", type: "ML" },
  { name: "Learning Leave", type: "LL" },
  { name: "Condolence Leave", type: "CL" },
  { name: "Paternity Leave", type: "PL" },
  { name: "Unpaid Leave", type: "UL" },
];

interface LeaveDrawerProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  handleClose: () => void;
  onSubmit: (leaveType: string, fromDate: string, toDate: string, reason: string) => void;
}

const LeaveDrawer: React.FC<LeaveDrawerProps> = ({
  isOpen,
  setIsOpen,
  handleClose,
  onSubmit,
}) => {
  const today = moment().format("YYYY-MM-DD");
  const [leaveType, setLeaveType] = useState<string>("AL");
  const [fromDate, setFromDate] = useState<string>(today);
  const [toDate, setToDate] = useState<string>(today);
  const [reason, setReason] = useState<string>("");
  const [error, setError] = useState<string>("");

  const canSubmit = leaveType && fromDate && toDate;

  const handleSubmit = () => {
    if (moment(toDate).isBefore(moment(fromDate))) {
      setError("End date cannot be before start date.");
      return;
    }
    setError("");
    onSubmit(leaveType, fromDate, toDate, reason);
  };

  const handleReset = () => {
    setLeaveType("AL");
    setFromDate(today);
    setToDate(today);
    setReason("");
    setError("");
  };

  return (
    <Drawer open={isOpen} setOpen={setIsOpen}>
      <Drawer.Panel className="max-w-[391px] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-3 border-b border-beerus">
          <h3 className="text-moon-20 font-bold transition-colors">
            Apply Leave
          </h3>
          <IconButton variant="ghost" onClick={handleClose}>
            <ControlsCloseSmall />
          </IconButton>
        </div>

        {/* Body */}
        <div className="p-6 flex-1 overflow-y-auto space-y-5">
          {/* Leave Type */}
          <div>
            <label className="block text-blackcst text-moon-14 mb-2 font-medium">
              Leave Type
            </label>
            <select
              value={leaveType}
              onChange={(e) => setLeaveType(e.target.value)}
              className="w-full border border-beerus rounded-moon-s-sm px-3 py-2 text-moon-14 bg-white focus:outline-none focus:border-frieza"
            >
              {LEAVE_TYPES.map((lt) => (
                <option key={lt.type} value={lt.type}>
                  {lt.name}
                </option>
              ))}
            </select>
          </div>

          {/* From Date */}
          <div>
            <label className="block text-blackcst text-moon-14 mb-2 font-medium">
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              min={today}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full border border-beerus rounded-moon-s-sm px-3 py-2 text-moon-14 bg-white focus:outline-none focus:border-frieza"
            />
          </div>

          {/* To Date */}
          <div>
            <label className="block text-blackcst text-moon-14 mb-2 font-medium">
              To Date
            </label>
            <input
              type="date"
              value={toDate}
              min={fromDate || today}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full border border-beerus rounded-moon-s-sm px-3 py-2 text-moon-14 bg-white focus:outline-none focus:border-frieza"
            />
          </div>

          {/* Duration display */}
          {fromDate && toDate && !moment(toDate).isBefore(moment(fromDate)) && (
            <p className="text-moon-14 text-gray-500">
              Duration:{" "}
              <span className="font-semibold text-blackcst">
                {moment(toDate).diff(moment(fromDate), "days") + 1} day(s)
              </span>
            </p>
          )}

          {/* Reason */}
          <div>
            <label className="block text-blackcst text-moon-14 mb-2 font-medium">
              Reason (optional)
            </label>
            <Input
              type="text"
              placeholder="Enter reason..."
              id="leave-reason"
              name="reason"
              autoComplete="off"
              value={reason}
              onChange={(e: any) => setReason(e.target.value)}
            />
          </div>

          {/* Error */}
          {error && (
            <p className="text-red-500 text-moon-12">{error}</p>
          )}
        </div>

        {/* Note */}
        <div className="px-[24px]">
          <div className="flex items-start space-x-2">
            <i className="text-moon-18 leading-1">
              <GenericAlarmRound />
            </i>
            <p className="text-moon-14 text-gray-600 flex-1">
              <span className="font-semibold"> Note:</span> Once submitted,
              your leave request will be reviewed by your manager.
            </p>
          </div>
        </div>

        <hr className="border-t border-beerus mt-[24px]" />

        {/* Footer */}
        <div className="p-6 flex items-center space-x-3">
          <div>
            <Button variant="ghost" onClick={handleReset}>
              Reset
            </Button>
          </div>
          <div className="flex items-center flex-1 justify-end space-x-3">
            <Button
              variant="secondary"
              onClick={() => {
                handleReset();
                handleClose();
              }}
            >
              Discard
            </Button>
            <Button
              className="bg-frieza"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              Apply
            </Button>
          </div>
        </div>
      </Drawer.Panel>
      <Drawer.Backdrop />
    </Drawer>
  );
};

export default LeaveDrawer;
